import { useEffect, useState } from 'hono/jsx';
import { Spinner } from '../../components/Spinner';
import { api } from '../../lib/api';

interface PaymentData {
  site: { name: string };
  plan: { name: string; description?: string; setupFee: number; subscription: number; interval: string; total: number };
  expiresAt: string;
  status?: 'paid' | 'expired' | 'canceled';
  paidAt?: string;
  error?: string;
}

export function Pay({ token }: { token: string }) {
  const [state, setState] = useState<'loading' | 'form' | 'error' | 'paid'>('loading');
  const [data, setData] = useState<PaymentData | null>(null);
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [checkingOut, setCheckingOut] = useState<boolean>(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('canceled') === 'true') { setError('Payment was canceled. You can try again when ready.'); setState('error'); return; }
    (async () => {
      try {
        const r = await api('/pay/' + token);
        const d = await r.json();
        if (!r.ok) {
          if (d.status === 'expired' || d.status === 'canceled') { setError(d.error); setState('error'); }
          else if (d.status === 'paid') { setData(d); setState('paid'); }
          else { setError(d.error || 'Payment link not found'); setState('error'); }
          return;
        }
        if (d.status === 'paid') { setData(d); setState('paid'); return; }
        setData(d); setState('form');
      } catch {
        setError('Failed to load payment details'); setState('error');
      }
    })();
  }, [token]);

  async function startCheckout() {
    setCheckingOut(true);
    try {
      const r = await api('/pay/' + token + '/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email || undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Failed to start checkout');
      window.location.href = d.checkoutUrl;
    } catch (err: any) {
      alert(err?.message || 'Failed to start checkout.');
      setCheckingOut(false);
    }
  }

  return (
    <div class="min-h-screen flex items-center justify-center p-4">
      <div class="w-full max-w-md">
        {state === 'loading' && (
          <div class="bg-dark-800 rounded-xl border border-slate-700/50 p-8 text-center">
            <div class="mx-auto"><Spinner /></div>
            <p class="text-slate-400 mt-4">Loading payment details…</p>
          </div>
        )}
        {state === 'error' && (
          <div class="bg-dark-800 rounded-xl border border-red-500/30 p-8 text-center">
            <div class="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </div>
            <h2 class="text-lg font-semibold text-slate-100 mb-2">Payment Link Unavailable</h2>
            <p class="text-slate-400 text-sm">{error}</p>
          </div>
        )}
        {state === 'paid' && (
          <div class="bg-dark-800 rounded-xl border border-emerald-500/30 p-8 text-center">
            <div class="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
            </div>
            <h2 class="text-lg font-semibold text-slate-100 mb-2">Already Paid</h2>
            <p class="text-slate-400 text-sm">This payment has already been completed.</p>
            {data?.paidAt && <p class="text-slate-500 text-xs mt-2">Paid on {new Date(data.paidAt).toLocaleDateString()}</p>}
          </div>
        )}
        {state === 'form' && data && (
          <>
            <div class="bg-dark-800 rounded-xl border border-slate-700/50 overflow-hidden">
              <div class="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 px-6 py-4 border-b border-slate-700/50">
                <h1 class="text-lg font-semibold text-slate-100">Complete Your Payment</h1>
                <p class="text-sm text-slate-400">For: {data.site.name}</p>
              </div>
              <div class="p-6 space-y-4">
                <div class="bg-dark-700 rounded-lg p-4">
                  <h3 class="text-slate-200 font-medium">{data.plan.name} Plan</h3>
                  <p class="text-sm text-slate-400 mt-1">{data.plan.description || 'Complete website package'}</p>
                  <div class="mt-3 pt-3 border-t border-slate-600/50 space-y-2">
                    {data.plan.setupFee > 0 && (
                      <div class="flex justify-between text-sm">
                        <span class="text-slate-400">Setup Fee</span>
                        <span class="text-slate-200 font-medium">${data.plan.setupFee.toLocaleString()}</span>
                      </div>
                    )}
                    {data.plan.subscription > 0 && (
                      <div class="flex justify-between text-sm">
                        <span class="text-slate-400">Subscription</span>
                        <span class="text-slate-200 font-medium">${data.plan.subscription.toLocaleString()}/{data.plan.interval}</span>
                      </div>
                    )}
                    <div class="flex justify-between text-sm pt-2 border-t border-slate-600/50">
                      <span class="text-slate-300 font-medium">Total Due Today</span>
                      <span class="text-cyan-400 font-semibold text-lg">${data.plan.total.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-1">Email (for receipt)</label>
                  <input
                    type="email" placeholder="your@email.com"
                    value={email}
                    onInput={(e: Event) => setEmail((e.currentTarget as HTMLInputElement).value)}
                    class="w-full px-3 py-2 bg-dark-700 border border-slate-600/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  />
                </div>
                <button
                  type="button" onClick={startCheckout} disabled={checkingOut}
                  class="w-full bg-cyan-500 text-dark-900 py-3 rounded-lg font-semibold hover:bg-cyan-400 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                  {checkingOut ? 'Redirecting…' : `Pay $${data.plan.total.toLocaleString()}`}
                </button>
                <p class="text-xs text-slate-500 text-center">Secure payment processed by Stripe. Your card details never touch our servers.</p>
              </div>
            </div>
            <p class="text-xs text-slate-500 text-center mt-4">This payment link expires on {new Date(data.expiresAt).toLocaleDateString()}</p>
          </>
        )}
      </div>
    </div>
  );
}
